/**
 * Table cell images: markdown + paste support without touching the milkdown fork.
 *
 * **Parse-time (`$remark`):** `remark-image-block` lifts lone `![…](…)` paragraphs to `image-block`
 * everywhere, including inside `tableCell`. GFM cells only allow `paragraph` children, so those
 * `image-block` nodes are invalid and get dropped. We rewrite `tableCell` children back to
 * `paragraph` → `image` (standard mdast) so the ProseMirror parser can build inline `image` nodes.
 *
 * **Paste-time:** Crepe’s bundled clipboard uses `handlePaste` and bypasses ProseMirror’s
 * `transformPasted`, so {@link createTableCellImagePasteProsePlugin} prepends and mirrors that
 * path when the selection is in a table cell. {@link createTableCellImageTransformPlugin} still
 * covers any default PM paste that does invoke `transformPasted`.
 */
import { DOMParser, DOMSerializer, Fragment, Slice } from '@milkdown/kit/prose/model';
import type { Node as PMNode, Schema } from '@milkdown/kit/prose/model';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { EditorView } from '@milkdown/kit/prose/view';
import type { Image, Paragraph, Root } from 'mdast';
import { visit } from 'unist-util-visit';

import { formatSizeTitle } from './platform-image-block/parse';
import { isInTableCellPos } from './tableCellHardBreak';

const IMAGE_BLOCK = 'image-block';

/** mdast node shape produced by `@milkdown/components` remark-image-block (not in stock mdast typings). */
type MdastImageBlock = {
  type: typeof IMAGE_BLOCK;
  url?: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
};

function isMdastImageBlock(node: { type?: string }): node is MdastImageBlock {
  return node.type === IMAGE_BLOCK;
}

/**
 * Walks the markdown AST after `remark-image-block` has run. Any `image-block` sitting directly
 * under a `tableCell` is converted to a single paragraph containing one standard `image` node,
 * which matches what GFM table cell parsers expect.
 */
export function revertImageBlocksToInlineInTableCells(tree: Root): void {
  visit(tree, 'tableCell', (cell) => {
    if (!('children' in cell) || !Array.isArray(cell.children)) return;

    for (let i = 0; i < cell.children.length; i++) {
      const child = cell.children[i] as { type?: string };
      if (!isMdastImageBlock(child)) continue;

      const sizeTitle = formatSizeTitle(child.width, child.height);
      const baseTitle = child.title ?? '';
      const title = sizeTitle ? (baseTitle ? `${baseTitle} ${sizeTitle}`.trim() : sizeTitle) : baseTitle;

      const inlineImage: Image = {
        type: 'image',
        url: child.url ?? '',
        alt: child.alt ?? '',
        title,
      };
      const paragraph: Paragraph = {
        type: 'paragraph',
        children: [inlineImage],
      };
      (cell.children as unknown[])[i] = paragraph;
    }
  });
}

/**
 * Builds a ProseMirror inline `image` node (commonmark) for use inside a table cell paragraph.
 * Block `image-block` uses `caption`; inline `image` uses `alt` for accessibility text.
 */
export function createCellImageNode(schema: Schema, src: string, alt: string, title = ''): PMNode | null {
  const img = schema.nodes.image;
  if (!img) return null;

  return img.create({ src, alt: alt || '', title });
}

/** Milkdown `parserCtx` return type for markdown/plain clipboard pastes. */
export type ClipboardMarkdownParser = (text: string) => PMNode | string | null | undefined;

/**
 * Returns true if the pasted slice contains at least one `image-block` node anywhere in its tree.
 */
export function sliceContainsImageBlock(slice: Slice): boolean {
  let found = false;
  slice.content.descendants((node) => {
    if (node.type.name === IMAGE_BLOCK) {
      found = true;

      return false;
    }

    return true;
  });

  return found;
}

/**
 * Recursively replaces `image-block` nodes with inline `image` nodes, preserving all other structure.
 * Used so a cut `image-block` becomes pastable content for a cell (which cannot host block images).
 */
export function mapImageBlocksToInlineImages(schema: Schema, node: PMNode): PMNode {
  const imgType = schema.nodes.image;
  if (node.type.name === IMAGE_BLOCK && imgType) {
    const caption = (node.attrs.caption as string | undefined) ?? '';
    // image-block has no `title` attr; Platform size lives on width/height (same as remark path).
    const title = formatSizeTitle(node.attrs.width as number | undefined, node.attrs.height as number | undefined);

    return imgType.create({
      src: node.attrs.src as string,
      alt: caption,
      title,
    });
  }

  if (!node.content.size) return node;

  const nextChildren: PMNode[] = [];
  let changed = false;
  node.content.forEach((child) => {
    const mapped = mapImageBlocksToInlineImages(schema, child);
    if (mapped !== child) changed = true;
    nextChildren.push(mapped);
  });

  if (!changed) return node;

  return node.copy(Fragment.fromArray(nextChildren));
}

/**
 * If the slice contains any `image-block`, returns a new slice with those mapped to inline `image`;
 * otherwise returns the original slice (same reference).
 */
export function inlineImageBlocksInSlice(schema: Schema, slice: Slice): Slice {
  if (!slice.content.size || !sliceContainsImageBlock(slice)) return slice;

  const top: PMNode[] = [];
  let changed = false;
  slice.content.forEach((node) => {
    const mapped = mapImageBlocksToInlineImages(schema, node);
    if (mapped !== node) changed = true;
    top.push(mapped);
  });

  if (!changed) return slice;

  return new Slice(Fragment.fromArray(top), slice.openStart, slice.openEnd);
}

/**
 * ProseMirror plugin: when the user pastes into a table cell and the clipboard slice contains
 * `image-block` nodes (e.g. cut from elsewhere in the doc), rewrite them to inline `image` so the
 * insert is schema-valid.
 */
export function createTableCellImageTransformPlugin() {
  return new Plugin({
    key: new PluginKey('table-cell-image-transform'),
    props: {
      transformPasted(slice, view: EditorView) {
        if (!sliceContainsImageBlock(slice)) return slice;
        if (!isInTableCellPos(view.state.selection.$from)) return slice;

        return inlineImageBlocksInSlice(view.state.schema, slice);
      },
    },
  });
}

const tableCellImagePasteKey = new PluginKey('brainfish-table-cell-image-paste');

/**
 * Builds the same {@link Slice} Crepe’s Milkdown clipboard `handlePaste` builds from `text/plain`
 * / `text/html` (see `@milkdown/plugin-clipboard`). Returns `null` when that path would bail early.
 */
export function buildMilkdownClipboardPasteSlice(
  schema: Schema,
  parser: ClipboardMarkdownParser,
  clipboardData: Pick<DataTransfer, 'getData'>,
): Slice | null {
  const text = clipboardData.getData('text/plain');
  const html = clipboardData.getData('text/html');
  if (html.length === 0 && text.length === 0) return null;

  const domParser = DOMParser.fromSchema(schema);
  let dom: globalThis.Node;
  if (html.length === 0) {
    const mdSlice = parser(text);
    if (!mdSlice || typeof mdSlice === 'string') return null;

    dom = DOMSerializer.fromSchema(schema).serializeFragment(mdSlice.content);
  } else {
    const template = document.createElement('template');
    template.innerHTML = html;
    dom = template.content.cloneNode(true);
    template.remove();
  }

  return domParser.parseSlice(dom);
}

/**
 * Prepends (via Milkdown `prosePluginsCtx`) so this runs before Crepe’s clipboard `handlePaste`.
 * Only intercepts pastes that would insert an `image-block` while the cursor is in a table cell.
 * This plugin only intercept pastes if `true` is returned from this function.
 */
export function createTableCellImagePasteProsePlugin(options: { getParser: () => ClipboardMarkdownParser; getSchema: () => Schema }) {
  return new Plugin({
    key: tableCellImagePasteKey,
    props: {
      handlePaste(view, event: ClipboardEvent, _slice?: Slice) {
        const schema = options.getSchema();
        const parser = options.getParser();
        const { clipboardData } = event;
        // Milkdown sets `editable` on the view via ctx; bare EditorViews omit it. Undefined means editable.
        const isEditable = view.props.editable?.(view.state);

        // Bail early if the document is not editable or there is no clipboard data
        if (isEditable === false || !clipboardData) {
          return false;
        }

        const text = clipboardData.getData('text/plain');
        const html = clipboardData.getData('text/html');
        // Bail early if there is no text or html in the clipboard data
        if (html.length === 0 && text.length === 0) {
          return false;
        }

        // Bail early if the clipboard data is from VS Code
        // prevent default milkdown behavior which creates a code block if pasting from VS Code
        const isFromVSCode = clipboardData.getData('vscode-editor-data');
        if (isFromVSCode) {
          return false;
        }

        // Bail early if the selection is not in a table cell
        if (!isInTableCellPos(view.state.selection.$from)) return false;

        // Build the slice from the clipboard data in order to detect if there are any image blocks
        const slice = buildMilkdownClipboardPasteSlice(schema, parser, clipboardData);
        if (!slice || !sliceContainsImageBlock(slice)) return false;

        // convert the image blocks to inline images
        const fixed = inlineImageBlocksInSlice(schema, slice);

        try {
          // Replace the selection with the new slice
          view.dispatch(view.state.tr.replaceSelection(fixed));

          return true;
        } catch {
          return false;
        }
      },
    },
  });
}
